import styled from "@emotion/styled";
import React from "react";
import {
  CaregiverComplianceItemMobile,
  CaregiverComplianceReferenceDocumentData,
  CaregiverDocumentTypeId,
  ComplianceStatus,
  TrainingCenterBundleDueDateId,
} from "../../../messages";
import { STRINGS } from "../../../strings";
import { TrainingCenterCaregiverBundle } from "../../../system/AppState/AppStateTrainingCenter";
import { translate } from "../../../translate";
import {
  getComplianceCardInfo,
  getInServiceComplianceCardInfo,
} from "../../../utils/complianceUtils";
import { AppCard, AppCardContent } from "../../Generic/AppCard";
import Flex from "../../Generic/Flex";
import NextButton from "../../NextButton/NextButton";
import ScaffoldTitle from "../../Scaffold/components/ScaffoldTitle";
import Scaffold from "../../Scaffold/Scaffold";
import { TrainingCenterLanguagePreference } from "../../TrainingCenter/TrainingCenterLanguageLookup";
import { getDisplayData } from "../../TrainingCenter/TrainingCenterUtils";
import ComplianceItemCard from "./ComplianceItemCard";
import ComplianceItemCardUI from "./ComplianceItemCardUI";
import EmptyBox from "./EmptyBox";
import { FilledCircledTick } from "./icons/FilledCircleTick";

type Props =
  | {
      isLoading: true;
      onClickGoBack: () => void;
    }
  | {
      isLoading: false;
      items: CaregiverComplianceItemMobile[];
      bundles: TrainingCenterCaregiverBundle[];
      languagePreference: TrainingCenterLanguagePreference;
      onClickComplianceItem: (item: CaregiverComplianceItemMobile) => void;
      onClickReferenceItem: (
        documentTypeId: CaregiverDocumentTypeId,
        referenceData: CaregiverComplianceReferenceDocumentData | null,
        complianceStatus: ComplianceStatus
      ) => void;
      onClickVerifiedItems: () => void;
      onClickBundle: (bundleDueDateId: TrainingCenterBundleDueDateId) => void;
      onClickGoBack: () => void;
    };

function MyMedflytComplianceScreen(props: Props) {
  const scaffoldTitle = <ScaffoldTitle label={translate(STRINGS.COMPLIANCE)} />;

  if (props.isLoading) {
    return <ScreenLoading {...props} scaffoldTitle={scaffoldTitle} />;
  }

  return <ScreenLoaded {...props} scaffoldTitle={scaffoldTitle} />;
}

function ScreenLoaded(props: Props & { isLoading: false; scaffoldTitle: React.ReactNode }) {
  const items = React.useMemo(
    () => props.items.filter((item) => !item.isHidden).map(getComplianceCardInfo),
    [props.items]
  );

  const overdueBundles = React.useMemo(
    () =>
      props.bundles.map(getInServiceComplianceCardInfo).filter((data) => data.type === "overdue"),
    [props.bundles]
  );

  const upcomingBundles = React.useMemo(
    () =>
      props.bundles.map(getInServiceComplianceCardInfo).filter((data) => data.type === "dueSoon"),
    [props.bundles]
  );

  const sections = React.useMemo(
    () => ({
      issues: {
        title: translate(STRINGS.ITEMS_WITH_ISSUES),
        items: items.filter((item) => item.type === "issue"),
        bundles: overdueBundles,
      },
      expiringSoon: {
        title: translate(STRINGS.ITEMS_THAT_WILL_EXPIRE_SOON),
        items: items.filter((item) => item.type === "expiresSoon" || item.type === "dueSoon"),
        bundles: upcomingBundles,
      },
      pending: {
        title: translate(STRINGS.PENDING_APPROVAL),
        items: items.filter((item) => item.type === "pending"),
        bundles: [],
      },
      compliant: {
        title: translate(STRINGS.COMPLIANT_ITEMS),
        items: items.filter((item) => item.type === "compliant"),
      },
    }),
    [items, overdueBundles, upcomingBundles]
  );

  return (
    <Scaffold
      title={props.scaffoldTitle}
      footer={
        <AppCard onClick={props.onClickVerifiedItems}>
          <AppCardContent>
            <Flex justifyContent="space-between" alignItems="center">
              <Flex xGap={8} alignItems="center">
                <FilledCircledTick />
                <ComplianceItemCardUI.Title>
                  {translate(STRINGS.ALL_VERIFIED_ITEMS)}
                </ComplianceItemCardUI.Title>
              </Flex>
              <Flex xGap={16} alignItems="center">
                <ComplianceItemCardUI.TextMuted>
                  {translate(STRINGS.NUM_COUNT_ITEMS).replace(
                    "{count}",
                    sections.compliant.items.length.toString()
                  )}
                </ComplianceItemCardUI.TextMuted>
                <NextButton />
              </Flex>
            </Flex>
          </AppCardContent>
        </AppCard>
      }
      onClickGoBack={props.onClickGoBack}
    >
      <MyMedflytComplianceScreen.Body>
        {items.length === sections.compliant.items.length &&
          overdueBundles.length === 0 &&
          upcomingBundles.length === 0 && (
            <EmptyBox
              title={translate(STRINGS.YOU_ARE_ALL_SET)}
              message={translate(STRINGS.NO_ISSUES_FOUND)}
            />
          )}

        {(["issues", "expiringSoon", "pending"] as const)
          .map((type) => [type, sections[type]] as const)
          .filter(([, section]) => section.items.length > 0 || section.bundles.length > 0)
          .map(([type, section]) => (
            <MyMedflytComplianceScreen.Section key={type}>
              <MyMedflytComplianceScreen.SectionTitle>
                {section.title}
              </MyMedflytComplianceScreen.SectionTitle>
              {section.items.map((data) =>
                data.item.referenceDocumentData === null ? (
                  <ComplianceItemCard
                    key={data.item.documentType.id.toString()}
                    {...data}
                    isLoading={false}
                    onClick={() => props.onClickComplianceItem(data.item)}
                  />
                ) : (
                  <ComplianceItemCard
                    key={data.item.documentType.id.toString()}
                    {...data}
                    isLoading={false}
                    onClick={() =>
                      props.onClickReferenceItem(
                        data.item.documentType.id,
                        data.item.referenceDocumentData,
                        data.item.complianceStatus
                      )
                    }
                  />
                )
              )}
              {section.bundles.map((data, i) => {
                const displayData = getDisplayData(
                  data.bundle.bundleDisplayData,
                  props.languagePreference
                );

                return (
                  <ComplianceItemCardUI
                    key={i}
                    isLoading={false}
                    title={displayData.data.title}
                    errorMessage={null}
                    icon={type === "issues" ? "error" : "warning"}
                    subTitle={data.subTitle}
                    footer={{
                      type: "fixIssue",
                      text: translate(STRINGS.WATCH_TRAINING_VIDEOS_TO_COMPLETE),
                    }}
                    onClick={() => props.onClickBundle(data.bundle.bundleDueDateId)}
                  />
                );
              })}
            </MyMedflytComplianceScreen.Section>
          ))}
      </MyMedflytComplianceScreen.Body>
    </Scaffold>
  );
}

function ScreenLoading(props: Props & { scaffoldTitle: React.ReactNode }) {
  return (
    <Scaffold title={props.scaffoldTitle} onClickGoBack={props.onClickGoBack}>
      <MyMedflytComplianceScreen.Body>
        <MyMedflytComplianceScreen.Section>
          {[...Array(12)].map((_, key) => (
            <ComplianceItemCardUI key={key} isLoading={true} />
          ))}
        </MyMedflytComplianceScreen.Section>
      </MyMedflytComplianceScreen.Body>
    </Scaffold>
  );
}

function TabTitle(props: { title: string; count: number }) {
  return (
    <TabTitle.Text>
      {props.title} ({props.count})
    </TabTitle.Text>
  );
}

TabTitle.Text = styled.span`
  text-transform: none;
`;

MyMedflytComplianceScreen.Body = styled.div`
  margin: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

MyMedflytComplianceScreen.Section = styled.div`
  display: flex;
  margin-top: 12px;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
`;

MyMedflytComplianceScreen.SectionTitle = styled.span`
  margin: 0;
`;

export default MyMedflytComplianceScreen;
